import { useEffect } from 'react';
import getConfig from 'next/config';
import { Button } from '@fabric-ds/react';
import logger from '@finn-no/fiaas-logger';

import { Head } from 'shared/page/Head';
import { Main } from 'shared/page/Main';

import type { ReqExtension } from '@/domain';
import { buildCommonProps } from '@/server/data/';
import { getInnfinnCsrfToken } from '@/utils/csrf';
import { trackAdInsertionInfoPageView } from '@/lib/tracking/tracking';
import { fetchPrivateItemLocation, type ItemLocation } from '@/api/objectApi';

const {
    publicRuntimeConfig: { baseUrl },
} = getConfig();

interface NewServiceInfo {
    itemId?: string;
    csrfMicroserviceToken: string;
}

const tjvrInfo = (actionPath: string, props: NewServiceInfo) => (
    <>
        <Head
            title="Hytteutleie | FINN reise"
            description="Informasjon om utleie av feriehjem og hytter i Norge"
            canonical={`${baseUrl}/reise/feriehus-hytteutleie/annonseinnlegging/info/`}
        />
        <Main broadcastUrl={'/broadcasts?path=www.finn.no/reise/feriehus-hytteutleie'} className="max-w-screen-md md:px-80 sm:px-16">
            <header>
                <h1 className="mt-64">Utleie av feriehjem og hytter i Norge</h1>
                <p className="mt-24">
                    Nå er det enda enklere og tryggere å leie ut feriehjem og hytter i Norge på FINN.no med
                    <strong> sikker betalingsløsning </strong> og <strong>gunstig forsikringsordning</strong>.
                </p>
            </header>
            <section>
                <h2 className="mt-32 h3">Rask guide i 4 steg for deg som leier ut i Norge</h2>
                <article>
                    <h3 className="mt-24 h4">(1) Trygg innbetaling</h3>
                    <p>
                        Du som utleier betaler ingenting (annonsen er gratis og tjenesten betales av leietaker). På FINN.no får du en sikker
                        utleieplattform med trygg betaling mellom privatpersoner. Leietaker betaler på forhånd, og pengene oppbevares hos
                        vår betalingspartner som overfører pengene trygt til deg 24 timer etter innsjekkingsdatoen.
                    </p>
                    <p className="mt-24">
                        Husk at utleieprisen nå vil være fastsatt så fort leietaker sender en forespørsel - det er derfor viktig at prisene
                        i kalenderen er korrekt, og du må selv sørge for at dette er oppdatert.
                    </p>
                    <p className="mt-24">
                        <strong>Sikre at du ikke går glipp av leieinntekter</strong>, husk å ha på notifications fra FINN mottar en
                        forespørsel om bestilling.
                    </p>
                </article>
                <article>
                    <h3 className="mt-32 h4">(2) Vertsforsikring</h3>
                    <p>
                        Vi gir deg Vertsforsikring fra If for utleiere. Dette er kostnadsfritt for deg som leier ut. Forsikringen dekker
                        skader forårsaket av leietaker eller dennes gjester med inntil 3 millioner kroner, med en egenandel på 2&nbsp;000
                        kroner per utleieforhold. Det er viktig å merke seg at Vertsforsikringen ikke erstatter din ordinære innbo- og
                        bygningsforsikring, og at du derfor må sørge for at du også har tegnet innbo-, hus- eller hytteforsikring selv.
                        Kunder av If med innbo-, hus- eller hytteforsikring vil ikke bli belastet med egenandel ved bruk av
                        Vertsforsikringen.
                    </p>
                </article>
                <article>
                    <h3 className="mt-32 h4">(3) Trygghet ved avbestilling</h3>
                    <p>
                        Velg mellom tre ulike avbestillingsregler som beskytter inntektene dine dersom leietakeren må avbestille oppholdet.
                    </p>
                </article>
                <article>
                    <h3 className="mt-32 h4">(4) Ting du burde vite før du leier ut</h3>
                    <ul className="flex flex-col gap-6">
                        <li className="list-disc ml-28">
                            <strong>BankID-verifisering:</strong> Som utleier må du verifisere deg med norsk BankID første gang du legger ut
                            en annonse.
                        </li>
                        <li className="list-disc ml-28">
                            <strong>Utleie for privatpersoner:</strong> FINN feriehjem og hytter er en utleieplattform som skal brukes av
                            privatpersoner.
                        </li>
                        <li className="list-disc ml-28">
                            <strong>Automatisk rapportering av skatt:</strong> Alt du får av leieinntekter rapporteres automatisk til
                            Skatteetaten. Du behøver ikke gjøre noe annet enn å dobbeltsjekke at skattemeldingen din stemmer.
                        </li>
                    </ul>
                </article>
            </section>
            <form action={actionPath} method={props.itemId ? 'GET' : 'POST'} className="mb-128 mt-32">
                <input type="hidden" name="adType" value="travel-holiday-rental" />
                <input type="hidden" name="__csrf_token" value={props.csrfMicroserviceToken} />
                <Button data-post-button type="submit" className="min-w-full button button--cta u-size1of1 u-pa16">
                    Kom i gang
                </Button>
            </form>
        </Main>
    </>
);

const NewServiceInfo = (props: NewServiceInfo) => {
    const actionPath = [innfinnBasePath, props.itemId].join('');

    useEffect(() => {
        trackAdInsertionInfoPageView(props.itemId);
    }, []);

    return tjvrInfo(actionPath, props);
};

const fltCookiesFrom = (req: ReqExtension) => {
    const lookupCookie = (name: string) => (req.cookies[name] ? `${name}=${req.cookies[name]}` : null);

    const flt = lookupCookie('__flt');
    const fltDev = lookupCookie('__flt_dev');

    return [flt, fltDev].filter(Boolean).join(';');
};

export const getServerSideProps = async ({ req, res, query }) => {
    const { abroad, itemId } = query;
    const commonProps = await buildCommonProps(req, res, query);

    if (itemId) {
        const location: ItemLocation = await fetchPrivateItemLocation(itemId);

        if (location?.abroad) {
            return {
                redirect: {
                    destination: `${innfinnBasePath}${itemId}`,
                    permanent: false,
                    basePath: false,
                },
            };
        }
    } else if (abroad === 'true') {
        // User clicked 'Create new ad' for abroad, i.e. should not show the page; instead create a new ad and redirect to it

        const formData = new FormData();
        formData.append('adType', 'travel-holiday-rental');
        formData.append('__csrf_token', getInnfinnCsrfToken(commonProps.loginId ?? 0) || '');

        const cookies = fltCookiesFrom(req);
        let response: Response;

        try {
            response = await fetch(`${baseUrl}${innfinnBasePath}`, {
                method: 'POST',
                redirect: 'manual',
                headers: { cookie: cookies },
                body: formData,
            });
        } catch (e) {
            logger.error('Could not init new ad in innfinn', e);
            throw new Error('Could not init new ad in innfinn');
        }

        if (response.status !== 302) {
            logger.error(`Unexpected response status from innfinn: ${response.status}`);
            throw new Error('Unexpected response status from innfinn');
        }

        const location = response.headers.get('location');

        return {
            redirect: {
                destination: location,
                permanent: false,
                basePath: false,
            },
        };
    }

    return {
        props: {
            itemId: itemId ?? null,
            csrfMicroserviceToken: getInnfinnCsrfToken(commonProps.loginId ?? 0),
            unleash: commonProps.unleash,
        },
    };
};

const innfinnBasePath = '/innfinn/adselection/ad/';

export default NewServiceInfo;
